import React, { useState, useEffect } from 'react';
import { useSessionLock } from '../../context/SessionLockContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Lock, LogOut, Shield, KeyRound, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export default function SessionLockModal() {
  const { user } = useAuth();
  const {
    isLocked,
    isLoggingOut,
    unlockError,
    unlockLoading,
    failedAttempts,
    unlockSession,
    manualLogoutFromLock,
    clearUnlockError
  } = useSessionLock();

  const [password, setPassword] = useState('');

  useEffect(() => {
    setPassword('');
    clearUnlockError();
  }, [isLocked, clearUnlockError]);

  if (!user || !isLocked) {
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password || isLoggingOut || unlockLoading) {
      return;
    }
    unlockSession(password);
  };

  const handleLogout = () => {
    if (isLoggingOut) {
      return;
    }
    manualLogoutFromLock();
  };

  const displayName = [user.name, user.surname].filter(Boolean).join(' ') || user.email || '';

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-xl px-4">
        <Card className="p-6 shadow-xl border border-border bg-background/95">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2">Sessione bloccata per inattività</h2>
              <div className="grid gap-2 text-xs sm:text-sm text-muted-foreground">
                <div className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
                  <div className="mt-0.5 text-primary">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-foreground">Privacy e riservatezza.</span>{' '}
                    Trattando dati personali e informazioni sensibili, l&apos;accesso viene bloccato automaticamente
                    dopo un periodo di inattività per ridurre il rischio di accessi non autorizzati.
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
                  <div className="mt-0.5 text-primary">
                    <KeyRound className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-foreground">Autenticazione aggiuntiva.</span>{' '}
                    Inserisci la tua password per sbloccare la sessione e riprendere il lavoro. In alternativa puoi
                    scegliere di <span className="font-semibold">uscire dall&apos;applicazione</span>.
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2">
                  <div className="mt-0.5 text-primary">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-foreground">Policy aziendali sulla protezione dei dati.</span>{' '}
                    Fare riferimento alle policy aziendali sulla protezione dei dati consultabili nel proprio profilo.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {displayName && (
            <div className="mb-4 text-sm text-muted-foreground">
              Utente corrente: <span className="font-medium text-foreground">{displayName}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                disabled={isLoggingOut || unlockLoading}
              />
            </div>

            {unlockError && (
              <div className="text-sm text-red-600 dark:text-red-400">
                {unlockError}
                {failedAttempts > 0 ? ` (Tentativi non riusciti: ${failedAttempts})` : ''}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1"
                disabled={!password || isLoggingOut || unlockLoading}
              >
                {unlockLoading ? 'Verifica...' : 'Sblocca'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
